// DeliveryActivity.js
// ---------------------------------------------------------------------------
// Delivery » Activity
//
// A bar chart of delivery activity over a date range.
//   • Site selector: "All sites" or one member (from localStorage "members").
//     If the org has a single member, that site is the default.
//   • Driver selector: "All drivers" or one driver at the chosen site.
//   • Date range: from / to. Default = the last month, ending today.
//   • Metric radios: Deliveries | Km | Time.
//   • X axis = individual days across the range. Y axis = total for the metric.
//     Deliveries mode stacks green (successful) / red (failed).
//   • Print button — prints the chart + the chosen parameters.
//
// Data source:
//   POST {API_BASE}/api/reports/delivery_activity
//     body: { accountIds: [], driverId|null, from, to }   (Bearer token)
//     returns: {
//       days:    [{ date:"YYYY-MM-DD", deliveries, failed, recipients, stops,
//                   miles, minutes }],
//       drivers: [{ driverId, driverName }]
//     }
//   POST {API_BASE}/api/reports/delivery_activity_drivers
//     body: { accountIds: [] }  -> { drivers:[{driverId,driverName}] }
//
// The old "xxx" sentinel for "whole fleet" is gone. The server fences every
// query to the branches on the caller's token, so an empty accountIds already
// means "everything I can see" and a client wildcard would be a request to
// widen scope rather than narrow it.
//
// Bars are timed on delivered_at, not on when the log reached the server: an
// offline round uploaded the next morning belongs on the day it was worked.
// ---------------------------------------------------------------------------
import API_BASE from "./apiBase";

import React, { useEffect, useMemo, useRef, useState } from "react";

const API_HOST = `${API_BASE}`;

// last-month default range: [today-1month, today], YYYY-MM-DD
function isoDay(d) {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
}
// ISO (YYYY-MM-DD) <-> UK display (DD/MM/YYYY)
function isoToUk(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}
function ukToIso(uk) {
  const m = String(uk || "").trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const dd = m[1].padStart(2, "0"), mm = m[2].padStart(2, "0"), yyyy = m[3];
  if (Number(mm) < 1 || Number(mm) > 12 || Number(dd) < 1 || Number(dd) > 31) return null;
  return `${yyyy}-${mm}-${dd}`;
}
// "2025-05-27" -> "Tue 27-05"  (weekday + dd-mm)
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function dayLabel(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso || "";
  const [y, m, d] = iso.split("-").map(Number);
  const wd = WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${wd} ${String(d).padStart(2, "0")}-${String(m).padStart(2, "0")}`;
}
// x-axis label: weekday names (daily-average mode) pass straight through;
// ISO dates get the "Tue 27-05" treatment.
function axisLabel(v) {
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? dayLabel(v) : v;
}
function defaultRange() {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - 1);
  return { from: isoDay(from), to: isoDay(to) };
}

const btn = {
  padding: "7px 13px", borderRadius: 8, border: "1px solid #d8dee8",
  background: "#fff", color: "#1f2a37", cursor: "pointer", fontSize: 14,
};
const label = { fontSize: 12, color: "#667085", marginBottom: 2, display: "block" };
const field = { padding: "7px 10px", borderRadius: 8, border: "1px solid #d8dee8", fontSize: 14 };

export default function DeliveryActivity({ userData, token }) {
  // --- members + org from localStorage (same as route_analysis) ---
  const members = useMemo(() => {
    try {
      const m = JSON.parse(localStorage.getItem("members") || "[]");
      return (Array.isArray(m) ? m : []).slice().sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || "")));
    } catch { return []; }
  }, []);
  const orgName = useMemo(() => {
    try { return (localStorage.getItem("organisationName") || "").trim(); } catch { return ""; }
  }, []);

  // Default site: sole member if exactly one, else "ALL".
  const soleMember = members.length === 1 ? members[0] : null;
  const [siteChoice, setSiteChoice] = useState(soleMember ? soleMember.name : "ALL");
  const [driverChoice, setDriverChoice] = useState("ALL");
  const [drivers, setDrivers] = useState([]);

  const initRange = defaultRange();
  const [from, setFrom] = useState(initRange.from);
  const [to, setTo] = useState(initRange.to);
  const [metric, setMetric] = useState("deliveries"); // deliveries | miles | time
  const [countBasis, setCountBasis] = useState("parcels"); // parcels | recipients | addresses (Deliveries only)
  const [dailyAvg, setDailyAvg] = useState(false);     // Mon–Sun weekday averages

  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const printRef = useRef(null);
  const plotRef = useRef(null);
  const [plotW, setPlotW] = useState(0);
  // Track the plot container's true width so the chart can fill available space
  // when there are few days. A ResizeObserver re-measures whenever layout
  // settles or the window resizes, avoiding the race where a one-shot measure
  // reads a stale/too-small width and the chart collapses on the next render.
  useEffect(() => {
    const el = plotRef.current;
    if (!el) return;
    const update = () => setPlotW(el.clientWidth);
    update();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    window.addEventListener("resize", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
  });

  const selectedSite = members.find((m) => m.name === siteChoice) || null;

  // Branch ids for the request body.
  //
  // The old "xxx" sentinel meant "the whole fleet" for the Pharmace admin
  // organisation. It is gone: the server fences every query to the branches on
  // the caller's token, so an empty list already means "everything I can see"
  // and a client-supplied wildcard would be a request to widen scope.
  const accountIdsPayload = () => {
    if (siteChoice === "ALL") {
      return Array.from(new Set((members || [])
        .map((m) => (m?.id != null ? String(m.id) : ""))
        .filter(Boolean)));
    }
    return selectedSite?.id ? [String(selectedSite.id)] : [];
  };

  const apiPost = async (path, body) => {
    const res = await fetch(`${API_HOST}/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`${path} → ${res.status}`);
    return res.json();
  };

  // Load drivers whenever the site changes (reset driver to ALL)
  useEffect(() => {
    let cancelled = false;
    setDriverChoice("ALL");
    (async () => {
      try {
        const data = await apiPost("api/reports/delivery_activity_drivers", { accountIds: accountIdsPayload() });
        if (!cancelled) setDrivers(Array.isArray(data?.drivers) ? data.drivers : []);
      } catch {
        if (!cancelled) setDrivers([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteChoice]);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const data = await apiPost("api/reports/delivery_activity", {
        accountIds: accountIdsPayload(),
        driverId: driverChoice === "ALL" ? null : driverChoice,
        from, to,
      });
      const rows = Array.isArray(data?.days) ? data.days : [];
      // normalise + ensure numeric
      setDays(rows.map((r) => ({
        date: r.date,
        deliveries: Number(r.deliveries || 0),
        failed: Number(r.failed || 0),
        successful: Math.max(0, Number(r.deliveries || 0) - Number(r.failed || 0)),
        recipients: Number(r.recipients || 0),
        addresses: Number(r.addresses || 0),
        miles: Number(r.miles || 0),
        minutes: Number(r.minutes || 0),
      })));
      if (Array.isArray(data?.drivers) && data.drivers.length) setDrivers(data.drivers);
    } catch (e) {
      setError(e.message || "Failed to load"); setDays([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh the chart whenever a data-affecting filter changes: site,
  // driver, or the date range. (Metric is NOT here — it only changes how the
  // already-loaded data is drawn, so the chart re-renders without a fetch.)
  // A short debounce coalesces the rapid site→driver-reset sequence into one
  // fetch and avoids a request per keystroke on the date fields.
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteChoice, driverChoice, from, to]);

  // chart config per metric. When dailyAvg is on, collapse the range into a
  // Monday–Sunday profile: for each weekday, average the metric across EVERY
  // occurrence of that weekday in the loaded range (denominator = number of
  // that weekday's calendar dates present in `days`, including zero-activity
  // ones — the backend already returns only active days, so we rebuild the
  // full weekday counts from the from/to span to keep the average honest).
  const WD_ORDER = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const chartData = React.useMemo(() => {
    if (!dailyAvg) return days;
    // Count how many of each weekday fall in [from,to] (all occurrences).
    const counts = { Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0 };
    const start = from && /^\d{4}-\d{2}-\d{2}$/.test(from) ? new Date(`${from}T00:00:00Z`) : null;
    const end = to && /^\d{4}-\d{2}-\d{2}$/.test(to) ? new Date(`${to}T00:00:00Z`) : null;
    if (start && end && start <= end) {
      const cur = new Date(start);
      while (cur <= end) {
        const wd = WD_ORDER[(cur.getUTCDay() + 6) % 7]; // Mon=0..Sun=6
        counts[wd]++;
        cur.setUTCDate(cur.getUTCDate() + 1);
      }
    }
    // Sum the metric fields per weekday from the active days we have.
    const sums = {};
    for (const wd of WD_ORDER) sums[wd] = { deliveries: 0, failed: 0, successful: 0, recipients: 0, addresses: 0, miles: 0, minutes: 0 };
    for (const d of days) {
      const [y, m, dd] = d.date.split("-").map(Number);
      const wd = WD_ORDER[(new Date(Date.UTC(y, m - 1, dd)).getUTCDay() + 6) % 7];
      sums[wd].deliveries += d.deliveries;
      sums[wd].failed += d.failed;
      sums[wd].successful += d.successful;
      sums[wd].recipients += d.recipients;
      sums[wd].addresses += d.addresses;
      sums[wd].miles += d.miles;
      sums[wd].minutes += d.minutes;
    }
    // Average = sum / (occurrences of that weekday in range), incl. zero days.
    return WD_ORDER.map((wd) => {
      const n = counts[wd] || 0;
      const avg = (v) => (n > 0 ? v / n : 0);
      return {
        date: wd,                       // used as the x label
        deliveries: Math.round(avg(sums[wd].deliveries) * 10) / 10,
        failed: Math.round(avg(sums[wd].failed) * 10) / 10,
        successful: Math.round(avg(sums[wd].successful) * 10) / 10,
        recipients: Math.round(avg(sums[wd].recipients) * 10) / 10,
        addresses: Math.round(avg(sums[wd].addresses) * 10) / 10,
        miles: Math.round(avg(sums[wd].miles) * 10) / 10,
        minutes: Math.round(avg(sums[wd].minutes)),
      };
    });
  }, [dailyAvg, days, from, to]);

  const deliveriesLabel = countBasis === "recipients" ? "Customers (unique)"
    : countBasis === "addresses" ? "Addresses (unique)" : "Parcels";
  const baseLabel = metric === "deliveries" ? deliveriesLabel
    : metric === "miles" ? "Miles" : "Time (minutes)";
  const yLabel = dailyAvg ? `${baseLabel} (daily average)` : baseLabel;
  // The green/red success/fail split only applies to Deliveries→Parcels.
  const stacked = metric === "deliveries" && countBasis === "parcels";

  const doPrint = () => {
    const title = "Delivery Activity";
    const paramLines = [
      `Site: ${siteChoice === "ALL" ? "All sites" : siteChoice}`,
      `Driver: ${driverChoice === "ALL" ? "All drivers" : (drivers.find(d => String(d.driverId) === String(driverChoice))?.driverName || driverChoice)}`,
      `Range: ${from} to ${to}`,
      `Metric: ${yLabel}`,
      ...(metric === "deliveries" ? [`Count by: ${countBasis.charAt(0).toUpperCase() + countBasis.slice(1)}`] : []),
      ...(dailyAvg ? ["View: Daily average (Mon–Sun)"] : []),
    ];
    const chartHtml = printRef.current ? printRef.current.innerHTML : "";
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>${title}</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;margin:24px;color:#111827}
      h1{font-size:18px} .params{font-size:13px;color:#374151;margin:8px 0 16px}
      .params div{margin:2px 0}</style></head><body>
      <h1>${title}</h1>
      <div class="params">${paramLines.map((l) => `<div>${l}</div>`).join("")}</div>
      <div>${chartHtml}</div>
      <script>window.onload=function(){window.print();}<\/script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ fontSize: 20, fontWeight: 600, margin: "0 0 12px" }}>Delivery Activity</h2>

      {/* controls */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end", marginBottom: 16 }}>
        <div>
          <label style={label}>Site</label>
          <select style={field} value={siteChoice} onChange={(e) => setSiteChoice(e.target.value)}>
            <option value="ALL">All sites</option>
            {members.map((m) => (
              <option key={m.id} value={m.name}>{m.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={label}>Driver</label>
          <select style={field} value={driverChoice} onChange={(e) => setDriverChoice(e.target.value)}>
            <option value="ALL">All drivers</option>
            {drivers.map((d) => (
              <option key={String(d.driverId)} value={String(d.driverId)}>{d.driverName || d.driverId}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={label}>From</label>
          <input type="text" placeholder="DD/MM/YYYY" style={{ ...field, width: 120 }}
            defaultValue={isoToUk(from)}
            onBlur={(e) => { const iso = ukToIso(e.target.value); if (iso) setFrom(iso); else e.target.value = isoToUk(from); }} />
        </div>
        <div>
          <label style={label}>To</label>
          <input type="text" placeholder="DD/MM/YYYY" style={{ ...field, width: 120 }}
            defaultValue={isoToUk(to)}
            onBlur={(e) => { const iso = ukToIso(e.target.value); if (iso) setTo(iso); else e.target.value = isoToUk(to); }} />
        </div>

        <div>
          <label style={label}>Metric</label>
          <div style={{ display: "flex", gap: 12, alignItems: "center", height: 34 }}>
            {[["deliveries", "Deliveries"], ["miles", "Miles"], ["time", "Time"]].map(([v, lbl]) => (
              <label key={v} style={{ fontSize: 14, cursor: "pointer" }}>
                <input type="radio" name="metric" value={v} checked={metric === v}
                  onChange={() => setMetric(v)} style={{ marginRight: 4 }} />
                {lbl}
              </label>
            ))}
          </div>
        </div>

        <div>
          <label style={{ ...label, color: metric === "deliveries" ? "#6b7280" : "#cbd5e1" }}>Count by</label>
          <div style={{ display: "flex", gap: 12, alignItems: "center", height: 34 }}>
            {[["parcels", "Parcels"], ["recipients", "Customers"]].map(([v, lbl]) => (
              <label key={v} style={{ fontSize: 14, cursor: metric === "deliveries" ? "pointer" : "default", color: metric === "deliveries" ? "#111827" : "#cbd5e1" }}
                title={v === "recipients" ? "Unique customers"
                  : v === "addresses" ? "Unique delivery addresses" : "Every parcel delivered"}>
                <input type="radio" name="countBasis" value={v} checked={countBasis === v}
                  disabled={metric !== "deliveries"}
                  onChange={() => setCountBasis(v)} style={{ marginRight: 4 }} />
                {lbl}
              </label>
            ))}
          </div>
        </div>

        <div>
          <label style={label}>&nbsp;</label>
          <label style={{ fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", height: 34 }}
            title="Collapse the range into a Monday–Sunday profile showing the average for each weekday.">
            <input type="checkbox" checked={dailyAvg} onChange={(e) => setDailyAvg(e.target.checked)} style={{ marginRight: 6 }} />
            Daily average
          </label>
        </div>

        <button style={{ ...btn, background: "#2a78d6", color: "#fff", border: "none" }}
          onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>
        <button style={{ ...btn, background: "#fff", color: "#111827", border: "1px solid #94a3b8", fontWeight: 600 }}
          onClick={doPrint} disabled={!days.length}>Print</button>
      </div>

      {error && <div style={{ color: "#b91c1c", marginBottom: 12 }}>{error}</div>}

      {/* chart — pure HTML/CSS bars (no recharts). Horizontally scrollable:
          each day is a fixed-width column, the track grows wider than the
          screen and scrolls. recharts' ResponsiveContainer renders empty
          inside an overflow:auto scroller, so we draw the bars directly. */}
      <div ref={printRef} style={{ background: "#fff", padding: 8 }}>
        {chartData.length > 0 && (() => {
          const COL_W = 34;          // px per day column
          const PLOT_H = 320;        // bar plotting area height
          // value selector for the current metric + (for deliveries) count basis
          const valOf = (d) => {
            if (metric === "miles") return d.miles;
            if (metric === "time") return d.minutes;
            // deliveries
            if (countBasis === "recipients") return d.recipients;
            if (countBasis === "addresses") return d.addresses;
            return d.deliveries; // parcels
          };
          const maxVal = Math.max(1, ...chartData.map(valOf));

          const bar = (d) => {
            // Deliveries→Parcels: stacked green (successful) / red (failed).
            if (stacked) {
              const succH = Math.round((d.successful / maxVal) * PLOT_H);
              const failH = Math.round((d.failed / maxVal) * PLOT_H);
              return (
                <div title={`${axisLabel(d.date)}\n${d.successful} successful, ${d.failed} failed`}
                  style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", height: PLOT_H }}>
                  <div style={{ height: failH, background: "#dc2626", borderRadius: "2px 2px 0 0" }} />
                  <div style={{ height: succH, background: "#16a34a" }} />
                </div>
              );
            }
            // Everything else: a single solid bar.
            const v = valOf(d);
            const h = Math.round((v / maxVal) * PLOT_H);
            const colour = metric === "miles" ? "#2a78d6"
              : metric === "time" ? "#7c3aed"
              : countBasis === "recipients" ? "#0891b2"      // customers
              : countBasis === "addresses" ? "#ca8a04"     // addresses
              : "#16a34a";                                 // parcels (non-split fallback)
            return (
              <div title={`${axisLabel(d.date)}\n${v}`}
                style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", height: PLOT_H }}>
                <div style={{ height: h, background: colour, borderRadius: "2px 2px 0 0" }} />
              </div>
            );
          };

          return (
            <div>
              {/* legend for deliveries */}
              {stacked && (
                <div style={{ display: "flex", gap: 16, marginBottom: 8, fontSize: 12 }}>
                  <span><span style={{ display: "inline-block", width: 12, height: 12, background: "#16a34a", marginRight: 4, verticalAlign: "middle" }} />Successful</span>
                  <span><span style={{ display: "inline-block", width: 12, height: 12, background: "#dc2626", marginRight: 4, verticalAlign: "middle" }} />Failed</span>
                </div>
              )}
              <div style={{ display: "flex", alignItems: "stretch" }}>
                {/* rotated axis title */}
                <div style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", fontSize: 12, color: "#374151", fontWeight: 600, textAlign: "center", paddingRight: 4, height: PLOT_H }}>
                  {yLabel}
                </div>

                {/* Y-axis scale: 5 evenly-spaced ticks with values + gridlines.
                    Values are dynamic to the selected metric's maximum. */}
                {(() => {
                  const TICKS = 5;
                  const fmtTick = (val) => {
                    if (metric === "miles") return val.toFixed(0);
                    return Math.round(val).toString();
                  };
                  return (
                    <div style={{ position: "relative", width: 46, height: PLOT_H, flexShrink: 0 }}>
                      {Array.from({ length: TICKS + 1 }).map((_, i) => {
                        const frac = i / TICKS;
                        const val = maxVal * (1 - frac);
                        return (
                          <div key={i} style={{ position: "absolute", top: `${frac * 100}%`, right: 4, transform: "translateY(-50%)", fontSize: 10, color: "#6b7280", whiteSpace: "nowrap" }}>
                            {fmtTick(val)}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* scrolling plot */}
                <div ref={plotRef} style={{ overflowX: "auto", overflowY: "hidden", flex: 1, borderLeft: "1px solid #d1d5db" }}>
                  {(() => {
                    const naturalW = chartData.length * COL_W;
                    // If the natural width fits, stretch columns to fill the
                    // container; otherwise keep fixed width and scroll.
                    const fill = plotW > 0 && naturalW < plotW;
                    const trackW = fill ? "100%" : naturalW;
                    const colStyle = fill
                      ? { flex: 1, minWidth: 0, padding: "0 3px", boxSizing: "border-box" }
                      : { width: COL_W, padding: "0 3px", boxSizing: "border-box" };
                    return (
                      <>
                        <div style={{ position: "relative", width: trackW, height: PLOT_H, borderBottom: "1px solid #d1d5db" }}>
                          {/* horizontal gridlines aligned to the Y ticks */}
                          {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} style={{ position: "absolute", left: 0, right: 0, top: `${(i / 5) * 100}%`, borderTop: "1px dashed #eef2f7" }} />
                          ))}
                          {/* bars */}
                          <div style={{ display: "flex", alignItems: "flex-end", width: "100%", height: "100%", position: "relative" }}>
                            {chartData.map((d) => (
                              <div key={d.date} style={colStyle}>
                                {bar(d)}
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* x-axis labels — rotated, anchored so the label's
                            top-right sits under the centre of its column. */}
                        <div style={{ display: "flex", width: trackW, marginTop: 4, height: 64 }}>
                          {chartData.map((d) => (
                            <div key={d.date} style={{ ...(fill ? { flex: 1, minWidth: 0 } : { width: COL_W }), display: "flex", justifyContent: "center", position: "relative" }}>
                              <span style={{ position: "absolute", top: 0, left: "50%", fontSize: 11, fontWeight: 700, color: "#374151", transform: "translateX(-100%) rotate(-45deg)", transformOrigin: "right top", whiteSpace: "nowrap" }}>
                                {axisLabel(d.date)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          );
        })()}

        {!loading && !days.length && (
          <div style={{ textAlign: "center", color: "#9ca3af", padding: 24 }}>
            No delivery activity for the selected filters.
          </div>
        )}
      </div>
    </div>
  );
}
